import { ArrowRightLeft, Factory, FileText, Gauge, ShieldCheck, Users } from 'lucide-react';
import type { NavigateFunction } from 'react-router-dom';
import {
  listCapas,
  listEcrs,
  listInstruments,
  listSuppliers,
  listUsers,
  search,
} from '@/api/dms/generated/sdk.gen';
import type { AsyncCommandProvider } from '@/components/command-palette';

export function createDmsCommandProviders(navigate: NavigateFunction): AsyncCommandProvider[] {
  return [
    // Documents — full-text search via /api/v1/search
    {
      id: 'dms:documents',
      kind: 'async',
      label: 'Documents',
      icon: FileText,
      priority: 20,
      scopeKeyword: 'docs',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: (q) => `/search?q=${encodeURIComponent(q)}`,
      search: async (query, { signal }) => {
        const { data } = await search({
          query: { q: query, per_page: 5, page: 1, status: 'DRAFT,IN_REVIEW,APPROVED' },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((doc: any) => ({
          id: `dms:doc:${doc.id}`,
          label: doc.title || 'Untitled',
          subtitle: [doc.document_number, doc.status].filter(Boolean).join(' · ') || undefined,
          icon: FileText,
          onSelect: () => navigate(`/documents/${doc.id}`),
        }));
      },
    },

    // CAPAs — search by CAR number + title
    {
      id: 'dms:capas',
      kind: 'async',
      label: 'CAPAs',
      icon: ShieldCheck,
      priority: 22,
      scopeKeyword: 'capas',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/capas',
      search: async (query, { signal }) => {
        const { data } = await listCapas({
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((capa: any) => ({
          id: `dms:capa:${capa.id}`,
          label: capa.title || capa.car_number,
          subtitle:
            [capa.car_number, capa.car_type, capa.stage].filter(Boolean).join(' · ') || undefined,
          icon: ShieldCheck,
          onSelect: () => navigate(`/capas/${capa.car_number}`),
        }));
      },
    },

    // ECRs — search by ECR number + title
    {
      id: 'dms:ecrs',
      kind: 'async',
      label: 'ECRs',
      icon: ArrowRightLeft,
      priority: 23,
      scopeKeyword: 'ecrs',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/ecrs',
      search: async (query, { signal }) => {
        const { data } = await listEcrs({
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((ecr: any) => ({
          id: `dms:ecr:${ecr.id}`,
          label: ecr.title || ecr.ecr_number,
          subtitle:
            [ecr.ecr_number, ecr.change_classification, ecr.stage].filter(Boolean).join(' · ') ||
            undefined,
          icon: ArrowRightLeft,
          onSelect: () => navigate(`/ecrs/${ecr.ecr_number}`),
        }));
      },
    },

    // Suppliers — search by name
    {
      id: 'dms:suppliers',
      kind: 'async',
      label: 'Suppliers',
      icon: Factory,
      priority: 25,
      scopeKeyword: 'suppliers',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/suppliers',
      search: async (query, { signal }) => {
        const { data } = await listSuppliers({
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((s: any) => ({
          id: `dms:supplier:${s.id}`,
          label: s.name,
          subtitle: [s.status, s.risk_level].filter(Boolean).join(' · ') || undefined,
          icon: Factory,
          onSelect: () => navigate(`/suppliers/${s.id}`),
        }));
      },
    },

    // Users — search by name/email
    {
      id: 'dms:users',
      kind: 'async',
      label: 'Users',
      icon: Users,
      priority: 27,
      scopeKeyword: 'users',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/admin/users',
      search: async (query, { signal }) => {
        const { data } = await listUsers({
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((u: any) => ({
          id: `dms:user:${u.id}`,
          label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
          subtitle: u.email,
          icon: Users,
          onSelect: () => navigate(`/admin/users/${u.id}`),
        }));
      },
    },

    // Calibration — search by equipment type, model, serial number
    {
      id: 'dms:calibration',
      kind: 'async',
      label: 'Calibration',
      icon: Gauge,
      priority: 28,
      scopeKeyword: 'cal',
      minQueryLength: 2,
      maxResults: 3,
      viewAllHref: () => '/calibration',
      search: async (query, { signal }) => {
        const { data } = await listInstruments({
          query: { search: query, per_page: 5, page: 1 },
          signal,
          throwOnError: true,
        });
        return (data?.items ?? []).map((i: any) => ({
          id: `dms:instrument:${i.id}`,
          label: `${i.equipment_type} — ${i.model}`,
          subtitle: [i.serial_number, i.status].filter(Boolean).join(' · ') || undefined,
          icon: Gauge,
          onSelect: () => navigate(`/calibration/${i.id}`),
        }));
      },
    },
  ];
}
